import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import {
  DndContext, DragOverlay, useDroppable, type CollisionDetection, type DragEndEvent, type DragMoveEvent,
  type DragStartEvent, type DraggableAttributes, type DraggableSyntheticListeners, type KeyboardCoordinateGetter,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Icon } from "./Icon";
import {
  DRAG_INSTRUCTIONS, SLIDE_MS, alwaysAnimate, dropAnimation, releasedOnControl, targetOf, useBuilderSensors,
  useNoPositionEndings, useReducedMotion, useStableIds,
} from "../routes/builderDrag";
import { dropPlan, findItem, indexAt, moveItem, type DayArrangement, type DropPlan } from "../lib/dayDrag";

/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * W137 cut 2 (R6) — MOVING A WORKOUT TO ANOTHER DAY, OR ABOVE/BELOW ANOTHER ON ITS DAY, BY ITS 4-ARROW HANDLE.
 *
 * ONE drag for the client Week (both views) and the Program board (cut 3) — his *"needs to look same like when we are
 * assigning workouts for a client and we drag things around"*. It is the BUILDER's contract (`routes/builderDrag.tsx`,
 * W96), not a second one: the page owns the pointer (no HTML5 `draggable`), the carried thing follows the pointer as an
 * overlay, ONE dashed gap travels as neighbours slide, every no-position ending cancels and the card goes home, the
 * keyboard path is dnd-kit's (Space/Enter, arrows, Escape) with the same spoken instructions. What a drop MEANS is the
 * page's (`onDrop` with `dayDrag.dropPlan`'s writes); `Move…` stays — the drag is only ever an accelerator.
 *
 * ⚠️ THE GAP IS A FIXED SMALL SLOT, NOT THE CARRIED ITEM'S HEIGHT (the builder's rule), deliberately: in Extended a
 * workout is up to ~3,000 px tall, and a gap that size would push every neighbour off the screen mid-drag. The overlay is
 * the workout's TITLE as a small card for the same reason.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

type Grip = { ref: (el: HTMLElement | null) => void; listeners: DraggableSyntheticListeners; attributes: DraggableAttributes } | null;
const GripContext = createContext<Grip>(null);
const ShownContext = createContext<{ arrangement: DayArrangement; active: string | null }>({ arrangement: {}, active: null });

/** The arrangement a page should DRAW: the live one while a drag (or its landing) is on, the page's otherwise. */
export function useDayArrangement(): { arrangement: DayArrangement; active: string | null } {
  return useContext(ShownContext);
}

/** The day under the pointer (or the overlay's centre, for the keyboard), and where among its items. */
export function dayCollision(ask: () => { self: string; arrangement: DayArrangement } | null): CollisionDetection {
  return ({ droppableContainers, pointerCoordinates, collisionRect }) => {
    const q = ask();
    if (q === null) return [];
    const p = pointerCoordinates ?? { x: collisionRect.left + collisionRect.width / 2, y: collisionRect.top + collisionRect.height / 2 };
    const byId = new Map(droppableContainers.map((c) => [String(c.id), c]));
    let hit: { id: string; dayKey: string } | null = null;
    for (const c of droppableContainers) {
      const d = c.data.current as { kind?: string; dayKey?: string } | undefined;
      if (d?.kind !== "day" || d.dayKey === undefined) continue;
      const r = c.node.current?.getBoundingClientRect();
      if (r === undefined) continue;
      if (p.x >= r.left && p.x <= r.right && p.y >= r.top && p.y <= r.bottom) { hit = { id: String(c.id), dayKey: d.dayKey }; break; }
    }
    if (hit === null) return [];
    const mids = (q.arrangement[hit.dayKey] ?? []).filter((id) => id !== q.self).map((id) => {
      const r = byId.get(`item:${id}`)?.node.current?.getBoundingClientRect();
      return r === undefined ? null : r.top + r.height / 2;
    }).filter((m): m is number => m !== null);
    return [{ id: hit.id, data: { target: { container: hit.id, section: null, index: indexAt(mids, p.y), dayKey: hit.dayKey } } }];
  };
}

/**
 * 🔴 THE KEYBOARD'S GEOMETRY IS A GRID OF DAYS. dnd-kit's sortable getter steps along ONE list, and MEASURED (drag.mjs's
 * keyboard arm) it returned no move at all across the calendar's days. So: ← / → carry the workout to the day beside (the
 * nearest day whose box overlaps this one's row), ↑ / ↓ to the slot above or below on its day, and past the first/last
 * item to the day above/below (the next week's row). The overlay's CENTRE is placed on the target — which is what
 * `dayCollision` reads for a keyboard drag.
 */
export const dayKeyboardCoordinates: KeyboardCoordinateGetter = (event, { context: { collisionRect, droppableContainers } }) => {
  const dirs = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
  if (!dirs.includes(event.code) || collisionRect === null) return undefined;
  event.preventDefault();
  const cx = collisionRect.left + collisionRect.width / 2, cy = collisionRect.top + collisionRect.height / 2;
  const days = droppableContainers.getEnabled().flatMap((c) => {
    const d = c.data.current as { kind?: string } | undefined;
    const r = c.node.current?.getBoundingClientRect();
    return d?.kind === "day" && r !== undefined ? [r] : [];
  });
  const here = days.find((r) => cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom);
  if (here === undefined) return undefined;
  const at = (x: number, y: number) => ({ x: x - collisionRect.width / 2, y: y - collisionRect.height / 2 });
  const overlapsRow = (r: DOMRect) => r.top < here.bottom && r.bottom > here.top;
  const overlapsCol = (r: DOMRect) => r.left < here.right && r.right > here.left;
  if (event.code === "ArrowRight" || event.code === "ArrowLeft") {
    const right = event.code === "ArrowRight";
    const next = days.filter((r) => overlapsRow(r) && (right ? r.left >= here.right - 1 : r.right <= here.left + 1))
      .sort((a, b) => (right ? a.left - b.left : b.left - a.left))[0];
    return next === undefined ? undefined : at(next.left + next.width / 2, Math.min(Math.max(cy, next.top + 8), next.bottom - 8));
  }
  const down = event.code === "ArrowDown";
  const step = Math.max(24, collisionRect.height);
  const y = cy + (down ? step : -step);
  if (y > here.top && y < here.bottom) return at(cx, y);
  const next = days.filter((r) => overlapsCol(r) && (down ? r.top >= here.bottom - 1 : r.bottom <= here.top + 1))
    .sort((a, b) => (down ? a.top - b.top : b.top - a.top))[0];
  return next === undefined ? undefined : at(next.left + next.width / 2, down ? next.top + 12 : next.bottom - 12);
};

/**
 * The area a drag lives in. `arrangement` is the page's (days in order, each with its ids top to bottom); `stored` each
 * id's stored order (`day_position`); `onDrop` is handed the plan and returns whether the page took it — a refusal sends
 * the item home. While the page's own read catches up after a drop, the dropped arrangement stays drawn (optimistic).
 */
export function DayDragArea({ arrangement, stored, onDrop, titleOf, disabled = false, children }: {
  arrangement: DayArrangement;
  stored: Readonly<Record<string, number>>;
  onDrop: (plan: DropPlan) => Promise<boolean>;
  /** What the overlay says while an item is carried: its workout's name. */
  titleOf: (id: string) => string;
  disabled?: boolean;
  children: ReactNode;
}) {
  const sensors = useBuilderSensors(dayKeyboardCoordinates);
  const reduced = useReducedMotion();
  const [active, setActive] = useState<string | null>(null);
  const [live, setLive] = useState<DayArrangement | null>(null);
  /* The dropped arrangement, drawn until the page's own arrangement changes (its re-read) or the write is refused. */
  const [landed, setLanded] = useState<DayArrangement | null>(null);
  const base = useRef<DayArrangement>(arrangement);
  const liveRef = useRef<DayArrangement | null>(null);
  liveRef.current = live;
  useEffect(() => { setLanded(null); }, [arrangement]);
  const shown = live ?? landed ?? arrangement;
  const collision = dayCollision(() => (active === null ? null : { self: active, arrangement: liveRef.current ?? base.current }));
  useNoPositionEndings(active !== null, () => undefined);

  function home() { setActive(null); setLive(null); }
  const onStart = ({ active: a }: DragStartEvent) => {
    base.current = landed ?? arrangement;
    setActive(String(a.id).replace(/^item:/, ""));
    setLive(base.current);
  };
  const onMove = ({ collisions }: DragMoveEvent) => {
    const t = targetOf(collisions) as ({ dayKey?: string; index: number } | null);
    if (active === null || t === null || t.dayKey === undefined) return;
    const now = liveRef.current ?? base.current;
    const at = findItem(now, active);
    if (at !== null && at.dayKey === t.dayKey && at.index === t.index) return;
    setLive(moveItem(now, active, t.dayKey, t.index));
  };
  const onEnd = ({ activatorEvent, delta }: DragEndEvent) => {
    const id = active;
    const final = liveRef.current;
    const ev = activatorEvent as PointerEvent | null;
    const released = ev !== null && "clientX" in ev && releasedOnControl(ev.clientX + delta.x, ev.clientY + delta.y);
    home();
    if (id === null || final === null || released) return;
    const plan = dropPlan(base.current, final, id, stored);
    if (plan === null) return;
    setLanded(final);
    void onDrop(plan).then((ok) => { if (!ok) setLanded(null); });
  };
  const title = active === null ? "" : titleOf(active);
  return (
    <DndContext sensors={sensors} collisionDetection={collision} onDragStart={onStart} onDragMove={onMove} onDragEnd={onEnd}
                onDragCancel={home}
                accessibility={{
                  screenReaderInstructions: DRAG_INSTRUCTIONS,
                  announcements: {
                    onDragStart: () => `Picked up ${title}.`,
                    onDragOver: () => undefined,
                    onDragMove: () => undefined,
                    onDragEnd: () => `Dropped ${title}.`,
                    onDragCancel: () => `Cancelled. ${title} went back to its place.`,
                  },
                }}>
      <ShownContext.Provider value={{ arrangement: shown, active }}>
        <DisabledContext.Provider value={disabled}>{children}</DisabledContext.Provider>
      </ShownContext.Provider>
      <DragOverlay dropAnimation={dropAnimation(reduced)} zIndex={40}>
        {active === null ? null : (
          <div className="day-drag-overlay">
            <Icon name="move" size={14} stroke={2} />
            <span>{title}</span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
const DisabledContext = createContext(false);

/** A day that takes drops: the caller's own element (a square card, a column) takes the ref, so the WHOLE day is the
 *  target. Its items are a sortable list in the order `useDayArrangement` draws. */
export function DropDay({ dayKey, ids, children }: {
  dayKey: string; ids: readonly string[];
  children: (ref: (el: HTMLElement | null) => void, isOver: boolean) => ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${dayKey}`, data: { kind: "day", dayKey } });
  const stable = useStableIds(ids.map((id) => `item:${id}`));
  return <SortableContext items={stable} strategy={verticalListSortingStrategy}>{children(setNodeRef, isOver)}</SortableContext>;
}

/** One draggable workout. While carried it is the GAP: a small dashed slot (`is-placeholder`), its contents hidden. */
export function DragItem({ id, className, locked = false, children }: {
  id: string; className?: string;
  /** A record that may not move (a TRAINED day — `menuItems` offers no Move… there): no handle, never carried. */
  locked?: boolean;
  children: ReactNode;
}) {
  const disabled = useContext(DisabledContext) || locked;
  const reduced = useReducedMotion();
  const { setNodeRef, setActivatorNodeRef, listeners, attributes, transform, transition, isDragging } = useSortable({
    id: `item:${id}`, disabled, data: { kind: "item" },
    animateLayoutChanges: reduced ? () => false : alwaysAnimate,
    transition: reduced ? null : { duration: SLIDE_MS, easing: "ease" },
  });
  const grip: Grip = disabled ? null : { ref: setActivatorNodeRef, listeners, attributes };
  return (
    <li ref={setNodeRef} data-drag-item={id}
        className={[className ?? "", isDragging ? "is-placeholder" : ""].filter(Boolean).join(" ")}
        style={{ transform: CSS.Translate.toString(transform), transition: transition ?? undefined }}>
      <GripContext.Provider value={grip}>{children}</GripContext.Provider>
    </li>
  );
}

/** THE 4-ARROW HANDLE (his: *"a drag icon like this so the 4 arrow thing"*), visible at rest, on every workout's title
 *  line; a mouse picks up on it after 4 px, a finger after a 150 ms hold, the keyboard with Space or Enter. */
export function MoveHandle({ label, onActivate }: { label: string; onActivate?: () => void }) {
  const grip = useContext(GripContext);
  if (grip === null) return null;
  /* R6b — a plain CLICK (no drag: the mouse sensor starts only after 4 px) opens the page's `Move…` window, so the icon is
     never a dead click; Space / Enter stay the keyboard pick-up (the drag's own keyboard path). */
  return (
    <button type="button" ref={grip.ref} className="day-move" title="Move" {...grip.attributes} {...grip.listeners}
            onClick={onActivate === undefined ? undefined : (e) => { if (e.detail > 0) onActivate(); }}
            aria-label={label}>
      <Icon name="move" size={14} stroke={2} />
    </button>
  );
}
